import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import type { BulkOutcome } from '../lib/bulk';
import { failed, succeeded } from '../lib/bulk';
import type { BatchResult } from '../lib/useBulkSelection';

/**
 * The "3 templates selected — [actions]" strip. Appears only once something is
 * ticked, and sticks to the top so the actions stay reachable while you scroll
 * a long list picking more.
 */
export function BulkBar({
  count,
  noun,
  total,
  onSelectAll,
  onClear,
  children,
}: {
  count: number;
  /** Singular; pluralised with a trailing "s". */
  noun: string;
  /** Total selectable rows, for the "select all N" shortcut. */
  total: number;
  onSelectAll: () => void;
  onClear: () => void;
  children: ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div className="sticky top-0 z-20 mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 shadow-sm">
      <span className="text-sm font-medium text-brand-800">
        {count} {noun}
        {count === 1 ? '' : 's'} selected
      </span>
      {count < total && (
        <button
          type="button"
          onClick={onSelectAll}
          className="text-xs font-medium text-brand-700 underline underline-offset-2 hover:text-brand-900"
        >
          Select all {total}
        </button>
      )}
      <div className="ml-auto flex flex-wrap items-center gap-2">{children}</div>
      <button
        type="button"
        onClick={onClear}
        aria-label="Clear selection"
        className="rounded p-1 text-brand-700 hover:bg-brand-100"
      >
        <X size={16} />
      </button>
    </div>
  );
}

/**
 * Outcome of a batch. Failures are listed individually with the server's own
 * message — "3 of 5 deleted" without saying *which* two survived or why is not
 * a result, it's a riddle.
 */
export function BulkResultNotice({
  results,
  verb,
  onDismiss,
}: {
  results: BulkOutcome[];
  /** Past tense, e.g. "deleted", "archived". */
  verb: string;
  onDismiss?: () => void;
}) {
  if (results.length === 0) return null;
  const ok = succeeded(results);
  const bad = failed(results);

  return (
    <div
      className={`mb-3 flex gap-2 rounded-lg p-3 text-sm ${
        bad.length ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-800'
      }`}
    >
      {bad.length ? (
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      ) : (
        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <span className="font-medium">
          {ok.length} {verb}
        </span>
        {bad.length > 0 && (
          <>
            <span>, {bad.length} failed:</span>
            <ul className="mt-1 space-y-0.5">
              {bad.map((o) => (
                <li key={o.id} className="truncate">
                  <span className="font-medium">{o.name}</span> — {o.error}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="shrink-0 rounded p-0.5">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/** `BulkResultNotice` fed straight from `useBulkSelection`'s batch state. */
export function BatchNotice({
  batch,
  onDismiss,
}: {
  batch: BatchResult | null;
  onDismiss: () => void;
}) {
  if (!batch) return null;
  return <BulkResultNotice results={batch.results} verb={batch.verb} onDismiss={onDismiss} />;
}
